/**
 * InlineEditSelector.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Seletor inline para edição pontual de campos no AppointmentDetailPanel.
 * Desktop: popover posicionado próximo ao elemento clicado.
 * Mobile: bottom sheet com overlay.
 *
 * @module InlineEditSelector
 */

import React, { useEffect, useRef, useState } from 'react';
import styles from './InlineEditSelector.module.css';

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const XMarkIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

// ── Componente principal ──────────────────────────────────────────────────────

/**
 * @param {object}   props
 * @param {boolean}  props.isOpen      - Controla visibilidade
 * @param {function} props.onClose     - Callback para fechar
 * @param {string}   props.title       - Título exibido no header
 * @param {Array}    props.options      - [{ id, label }]
 * @param {*}        props.selectedId  - ID da opção atualmente selecionada
 * @param {function} props.onSelect    - Callback ao selecionar: (option) => void
 * @param {Element}  props.anchorEl    - Elemento DOM de referência para posicionamento
 */
const InlineEditSelector = ({ isOpen, onClose, title, options, selectedId, onSelect, anchorEl }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);

  // ── Detectar mobile ────────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Calcular posição do popover desktop ────────────────────────────────────
  useEffect(() => {
    if (isOpen && anchorEl && !isMobile) {
      const rect = anchorEl.getBoundingClientRect();
      const popoverWidth = 280;
      const viewportWidth = window.innerWidth;

      let left = rect.left;
      if (left + popoverWidth > viewportWidth - 16) {
        left = viewportWidth - popoverWidth - 16;
      }
      if (left < 8) left = 8;

      setPosition({
        top: rect.bottom + 8,
        left,
      });
    }
  }, [isOpen, anchorEl, isMobile]);

  // ── Fechar ao clicar fora ou pressionar Escape ─────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        if (anchorEl && anchorEl.contains(e.target)) return;
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, anchorEl]);

  if (!isOpen) return null;

  const handleSelect = (option) => {
    onSelect(option);
    onClose();
  };

  // Compara IDs de forma segura: null/undefined são tratados como "sem seleção"
  const isSelected = (optionId, currentId) => {
    if (optionId == null && (currentId == null || currentId === '' || currentId === '0' || currentId === 0)) return true;
    if (optionId == null || currentId == null) return false;
    return String(optionId) === String(currentId);
  };

  // ── Mobile: Bottom Sheet ───────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <div className={styles.overlay} onClick={onClose} />
        <div className={styles.bottomSheet} ref={popoverRef} role="dialog" aria-modal="true" aria-label={title}>
          <div className={styles.handle} aria-hidden="true" />
          <div className={styles.sheetHeader}>
            <span className={styles.sheetTitle}>{title}</span>
            <button className={styles.closeButton} onClick={onClose} aria-label="Fechar" type="button">
              <XMarkIcon size={20} />
            </button>
          </div>
          <div className={styles.optionsList}>
            {options.map((option) => (
              <button
                key={option.id ?? '__none__'}
                className={`${styles.optionItem} ${isSelected(option.id, selectedId) ? styles.optionSelected : ''}`}
                onClick={() => handleSelect(option)}
                type="button"
              >
                <span className={styles.checkIcon}>
                  {isSelected(option.id, selectedId) && <CheckIcon />}
                </span>
                <span className={styles.optionLabel}>{option.label}</span>
              </button>
            ))}
            {options.length === 0 && (
              <div className={styles.emptyState}>Nenhuma opção disponível</div>
            )}
          </div>
        </div>
      </>
    );
  }

  // ── Desktop: Popover ───────────────────────────────────────────────────────
  return (
    <div
      ref={popoverRef}
      className={styles.popover}
      style={{ top: position.top, left: position.left }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className={styles.popoverHeader}>
        <span className={styles.popoverTitle}>{title}</span>
        <button className={styles.closeButton} onClick={onClose} aria-label="Fechar" type="button">
          <XMarkIcon size={18} />
        </button>
      </div>
      <div className={styles.optionsList}>
        {options.map((option) => (
          <button
            key={option.id ?? '__none__'}
            className={`${styles.optionItem} ${isSelected(option.id, selectedId) ? styles.optionSelected : ''}`}
            onClick={() => handleSelect(option)}
            type="button"
          >
            <span className={styles.checkIcon}>
              {isSelected(option.id, selectedId) && <CheckIcon />}
            </span>
            <span className={styles.optionLabel}>{option.label}</span>
          </button>
        ))}
        {options.length === 0 && (
          <div className={styles.emptyState}>Nenhuma opção disponível</div>
        )}
      </div>
    </div>
  );
};

export default InlineEditSelector;
